import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  ReactNode,
} from 'react'
import type { OrderLine, Portion, Product, Tare, WeighPlate } from '../types'
import { applyWeigh, newWeighId, withQty } from '../utils/lineMath'

type Action =
  | { type: 'add'; product: Product; station?: string; byName?: string; portion?: Portion }
  | { type: 'increment'; key: string }
  | { type: 'decrement'; key: string }
  | { type: 'remove'; key: string }
  | { type: 'setPortionNotes'; productId: string; notes: string[]; byName?: string; portion?: string }
  | { type: 'setUnitPrice'; key: string; unitPriceKurus: number }
  | { type: 'addPendingWeigh'; product: Product; station?: string; byName?: string }
  | { type: 'addWeighed'; product: Product; tare?: Tare; plates: WeighPlate[]; station?: string; byName?: string }
  | { type: 'applyWeigh'; key: string; tare?: Tare; plates: WeighPlate[]; weighedBy?: string }
  | { type: 'replace'; lines: OrderLine[] }
  | { type: 'clear' }

/**
 * Identifies a cart line. Product alone isn't enough once notes exist: a table
 * ordering one with onion and one without needs two lines of the same product,
 * each carrying its own note.
 *
 * The price is part of it too, because an item sold by weight can appear twice
 * at different prices. Without it both rows would answer to the same key and a
 * quantity button would move the wrong one.
 */
export function lineKey(
  line: Pick<OrderLine, 'productId' | 'note' | 'unitPriceKurus' | 'byName' | 'weigh' | 'portion'>,
): string {
  // Who rang it up is part of the identity too. A table opened by one waiter
  // and added to by another would otherwise merge both rounds into one line,
  // and the question "who put this on my table" would have no answer.
  // Tartılı satırda tartımın kimliği de: aynı börek iki tartımla iki satır.
  // Porsiyon yalnız varsa eklenir: porsiyonsuz satırların anahtarı eskisi gibi.
  const base = `${line.productId} ${line.unitPriceKurus} ${line.note ?? ''} ${line.byName ?? ''} ${line.weigh?.id ?? ''}`
  return line.portion ? `${base} ${line.portion}` : base
}

/**
 * Lines carry a copy of the name and unit price rather than a product
 * reference: the price that goes on the receipt is the one showing when the
 * item was rung up, even if the menu is edited a minute later.
 */
function reducer(state: OrderLine[], action: Action): OrderLine[] {
  switch (action.type) {
    case 'add': {
      const { product, portion } = action
      // Porsiyon seçildiyse (3 Kas 2026) fiyat onun, ad "Ürün (1,5 Porsiyon)":
      // fiş, mutfak ve rapor porsiyonu ek iş olmadan gösteriyor.
      const price = portion ? portion.priceKurus : product.priceKurus
      const name = portion ? `${product.name} (${portion.name})` : product.name
      // A tap adds to the un-noted line at the menu price. Noted lines are only
      // reached by editing an existing one, and a line whose price was corrected
      // for weight starts a new row rather than absorbing this one, the next
      // portion goes on the scale by itself.
      const key = lineKey({
        productId: product.id,
        unitPriceKurus: price,
        byName: action.byName,
        portion: portion?.name,
      })
      const existing = state.find((l) => lineKey(l) === key)
      if (existing) {
        return state.map((l) =>
          lineKey(l) === key
            ? { ...l, qty: l.qty + 1, lineTotalKurus: (l.qty + 1) * l.unitPriceKurus }
            : l,
        )
      }
      return [
        ...state,
        {
          productId: product.id,
          name,
          unitPriceKurus: price,
          qty: 1,
          lineTotalKurus: price,
          ...(portion ? { portion: portion.name } : {}),
          // Copied now, not looked up later: moving a category to another
          // counter must not reroute slips that were already printed.
          station: action.station,
          byName: action.byName,
        },
      ]
    }

    // Tartılmış satırın adedi tabak sayısı: artırılamaz, azaltınca satır
    // tümden gidiyor (yeniden tartılır). Bekleyen satırda tutar 0 kalıyor.
    case 'increment': {
      const src = state.find((l) => lineKey(l) === action.key)
      if (!src || src.weigh?.status === 'done') return state
      // Notlu satırda "+" notsuz yeni porsiyon ekliyor (kullanıcı, 22 Eki
      // 2026): ikinci köfte birincinin "soğansız"ını alıyordu. Aynı fiyatlı
      // notsuz satır varsa o artıyor, yoksa notlunun hemen altına açılıyor.
      if (src.note && !src.weigh) {
        const { note: _n, ...plain } = src
        const plainKey = lineKey(plain)
        if (state.some((l) => lineKey(l) === plainKey)) {
          return state.map((l) => (lineKey(l) === plainKey ? withQty(l, l.qty + 1) : l))
        }
        const at = state.indexOf(src) + 1
        return [...state.slice(0, at), withQty(plain, 1), ...state.slice(at)]
      }
      return state.map((l) => (lineKey(l) === action.key ? withQty(l, l.qty + 1) : l))
    }

    case 'decrement':
      return state.flatMap((l) => {
        if (lineKey(l) !== action.key) return [l]
        if (l.qty <= 1 || l.weigh?.status === 'done') return []
        return [withQty(l, l.qty - 1)]
      })

    /**
     * Masada tartılı ürün: "tartım bekliyor" satırı. Aynı kişinin bekleyen
     * satırı varsa adet ona ekleniyor; tutar tartılana kadar 0.
     */
    case 'addPendingWeigh': {
      const { product } = action
      const at = state.findIndex(
        (l) =>
          l.productId === product.id &&
          l.weigh?.status === 'pending' &&
          (l.byName ?? '') === (action.byName ?? ''),
      )
      if (at >= 0) return state.map((l, i) => (i === at ? withQty(l, l.qty + 1) : l))
      return [
        ...state,
        {
          productId: product.id,
          name: product.name,
          unitPriceKurus: product.priceKurus,
          qty: 1,
          lineTotalKurus: 0,
          station: action.station,
          byName: action.byName,
          weigh: { id: newWeighId(), status: 'pending', perKgKurus: product.priceKurus },
        },
      ]
    }

    /** Gel-al: tartım ekranından çıkan, tartılmış yeni satır. */
    case 'addWeighed': {
      const { product } = action
      const line: OrderLine = {
        productId: product.id,
        name: product.name,
        unitPriceKurus: product.priceKurus,
        qty: action.plates.length,
        lineTotalKurus: 0,
        station: action.station,
        byName: action.byName,
        weigh: { id: newWeighId(), status: 'pending', perKgKurus: product.priceKurus },
      }
      return [...state, applyWeigh(line, action.tare, action.plates, action.byName)]
    }

    /** Bekleyen (ya da yeniden tartılan) satırın tartımı. */
    case 'applyWeigh':
      return state.map((l) =>
        lineKey(l) === action.key && l.weigh ? applyWeigh(l, action.tare, action.plates, action.weighedBy) : l,
      )

    case 'remove':
      return state.filter((l) => lineKey(l) !== action.key)

    /**
     * Overrides the unit price of one line, for something sold by weight where
     * the menu price is only a starting point.
     *
     * The line's own copy of the price is what a receipt and a report read, so
     * changing it here is enough, the menu is deliberately left alone, and the
     * next customer gets the listed price again.
     */
    case 'setUnitPrice': {
      const unitPriceKurus = Math.max(0, Math.round(action.unitPriceKurus))
      return state.map((l) =>
        lineKey(l) === action.key && !l.weigh
          ? { ...l, unitPriceKurus, lineTotalKurus: l.qty * unitPriceKurus }
          : l,
      )
    }

    /**
     * Replaces every note for one product with a per-portion list: notes[0] is
     * the first Adana, notes[1] the second, and so on. Portions sharing a note
     * collapse into one line, so "soğansız, soğansız, düz" becomes two rows.
     *
     * Whole-product rather than per-line because the dialog shows all portions
     * of that product at once, and a previous edit may already have split them
     * across several lines.
     */
    case 'setPortionNotes': {
      const { productId, notes, byName } = action
      // Porsiyonlar ayrı (3 Kas 2026): 1,5 porsiyonun notu 1 porsiyonu yeniden kurmasın.
      const samePortion = (l: OrderLine) => (l.portion ?? '') === (action.portion ?? '')
      /**
       * Only this person's portions of the product.
       *
       * The rebuild below deletes every matching line and writes fresh ones, so
       * without this filter editing your own note rewrote a colleague's round
       * of the same item, their soğansız became yours, or vanished. On a table
       * two waiters are both serving, that is someone else's order changed by
       * a tap that had nothing to do with it.
       */
      // Tartılı satırlar not düzenlemesinin dışında: yeniden kurulurken
      // tabakları kaybolurdu.
      const mine = state.filter(
        (l) => l.productId === productId && (l.byName ?? '') === (byName ?? '') && !l.weigh && samePortion(l),
      )
      if (mine.length === 0) return state

      const template = mine[0]
      const byNote = new Map<string, number>()
      for (const raw of notes) {
        const note = raw.trim()
        byNote.set(note, (byNote.get(note) ?? 0) + 1)
      }

      // Lines are rebuilt from the first one, so a price corrected on a later
      // line would be lost. Where every line agrees on a price that is the one
      // to keep; where they differ, editing notes is not the place to pick a
      // winner, so the first line's price stands.
      const prices = new Set(mine.map((l) => l.unitPriceKurus))
      const unitPriceKurus =
        prices.size === 1 ? [...prices][0] : template.unitPriceKurus

      // Rebuilt in the order the notes were listed, and dropped in where the
      // product's first line was so the cart doesn't reshuffle under the hand.
      const rebuilt: OrderLine[] = [...byNote.entries()].map(([note, qty]) => {
        // The key is dropped rather than set to undefined. Firestore rejects an
        // undefined value outright, and the write that carried it failed with
        // nothing on screen to say so, a waiter noting four portions where one
        // was left plain got no ticket, no slip and no error.
        const line: OrderLine = {
          ...template,
          qty,
          unitPriceKurus,
          lineTotalKurus: qty * unitPriceKurus,
        }
        if (note) line.note = note
        else delete line.note
        return line
      })

      let placed = false
      return state.flatMap((l) => {
        // Someone else's portions of the same product pass through untouched.
        if (l.productId !== productId || (l.byName ?? '') !== (byName ?? '') || l.weigh || !samePortion(l)) {
          return [l]
        }
        if (placed) return []
        placed = true
        return rebuilt
      })
    }

    // Loading an existing ticket. Its lines already carry the prices they
    // were rung up at, so they are taken as-is rather than re-priced.
    case 'replace':
      return action.lines.map((l) => ({ ...l }))

    case 'clear':
      return []
  }
}

/**
 * Which sale the cashier is ringing up. The two are kept apart rather than
 * sharing one basket: a walk-up customer standing at the till must not have
 * their tea land on the table ticket that was left half-built, and coming
 * back to a tab should find it exactly as it was.
 */
export type CartTab = 'takeaway' | 'table'

export const CART_TABS: CartTab[] = ['takeaway', 'table']

const TAB_KEY = 'adissoo.cartTab'

interface CartValue {
  lines: OrderLine[]
  totalKurus: number
  itemCount: number
  isEmpty: boolean
  /** Which basket the buttons below act on. */
  tab: CartTab
  setTab: (tab: CartTab) => void
  add: (product: Product, station?: string, byName?: string, portion?: Portion) => void
  increment: (key: string) => void
  decrement: (key: string) => void
  remove: (key: string) => void
  /** One note per portion of a product, in order. Same notes merge into a line. */
  setPortionNotes: (productId: string, notes: string[], byName?: string, portion?: string) => void
  /** Overrides one line's unit price; the menu keeps its own. */
  setUnitPrice: (key: string, unitPriceKurus: number) => void
  /** Masada tartılı ürün: tartım bekleyen satır. */
  addPendingWeigh: (product: Product, station?: string, byName?: string) => void
  /** Gel-al: tartılmış satır. */
  addWeighed: (product: Product, tare: Tare | undefined, plates: WeighPlate[], station?: string, byName?: string) => void
  /** Bekleyen satırı tartar (ya da yeniden tartar). */
  applyWeigh: (key: string, tare: Tare | undefined, plates: WeighPlate[], weighedBy?: string) => void
  replace: (lines: OrderLine[]) => void
  /**
   * Loads lines into a named basket rather than the active one. Needed because
   * setTab only takes effect on the next render, so a replace() right after it
   * would still write to the basket being left.
   */
  replaceIn: (tab: CartTab, lines: OrderLine[]) => void
  clear: () => void
  /** Empties a named basket, for the same reason replaceIn exists. */
  clearIn: (tab: CartTab) => void
}

const CartContext = createContext<CartValue | undefined>(undefined)

/**
 * Runs the line reducer on whichever basket the action names, leaving the
 * other one untouched.
 */
function tabsReducer(
  state: Record<CartTab, OrderLine[]>,
  action: Action & { tab: CartTab },
): Record<CartTab, OrderLine[]> {
  const next = reducer(state[action.tab], action)
  if (next === state[action.tab]) return state
  return { ...state, [action.tab]: next }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [carts, dispatchTab] = useReducer(tabsReducer, {
    takeaway: [],
    table: [],
  })
  // Yenilemeden sağ çıkıyor: Masa'dayken sayfayı yenileyen kasiyer Gel-Al'a
  // düşüyordu. sessionStorage, çünkü bu o pencerenin hali, aynı cihazda
  // açılan ikinci bir sekme kendi yerinden başlamalı.
  const [tab, setTab] = useState<CartTab>(() => {
    try {
      return sessionStorage.getItem(TAB_KEY) === 'table' ? 'table' : 'takeaway'
    } catch {
      return 'takeaway'
    }
  })

  useEffect(() => {
    try {
      sessionStorage.setItem(TAB_KEY, tab)
    } catch {
      // Gizli pencere ya da kapalı depolama: yenileyince Gel-Al'dan başlar.
    }
  }, [tab])

  const lines = carts[tab]

  // Always aimed at the basket on screen, so a stale closure can't drop a
  // tapped item into the one the cashier just left.
  const dispatch = useCallback(
    (action: Action) => dispatchTab({ ...action, tab }),
    [tab],
  )

  // Lets the service worker hold a pending update back until the sale in
  // progress is finished, see main.tsx. Both baskets count: reloading with a
  // half-built table ticket in the background would lose it just the same.
  useEffect(() => {
    window.__adissooCartBusy = carts.takeaway.length > 0 || carts.table.length > 0
  }, [carts])

  const value = useMemo<CartValue>(
    () => ({
      lines,
      totalKurus: lines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
      itemCount: lines.reduce((sum, l) => sum + l.qty, 0),
      isEmpty: lines.length === 0,
      tab,
      setTab,
      add: (product, station, byName, portion) =>
        dispatch({ type: 'add', product, station, byName, portion }),
      increment: (key) => dispatch({ type: 'increment', key }),
      decrement: (key) => dispatch({ type: 'decrement', key }),
      remove: (key) => dispatch({ type: 'remove', key }),
      setPortionNotes: (productId, notes, byName, portion) =>
        dispatch({ type: 'setPortionNotes', productId, notes, byName, portion }),
      setUnitPrice: (key, unitPriceKurus) =>
        dispatch({ type: 'setUnitPrice', key, unitPriceKurus }),
      addPendingWeigh: (product, station, byName) =>
        dispatch({ type: 'addPendingWeigh', product, station, byName }),
      addWeighed: (product, tare, plates, station, byName) =>
        dispatch({ type: 'addWeighed', product, tare, plates, station, byName }),
      applyWeigh: (key, tare, plates, weighedBy) =>
        dispatch({ type: 'applyWeigh', key, tare, plates, weighedBy }),
      replace: (lines) => dispatch({ type: 'replace', lines }),
      replaceIn: (which, lines) => dispatchTab({ type: 'replace', lines, tab: which }),
      clear: () => dispatch({ type: 'clear' }),
      clearIn: (which) => dispatchTab({ type: 'clear', tab: which }),
    }),
    [lines, carts, tab, dispatch],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within a CartProvider')
  return ctx
}
